import * as React from "react";
import { useEffect, useRef, useState } from "react";
import {
  Box, Button, Center, Divider, Flex, Grid, HStack, Heading, Input, Radio, Select, Stack, Tab, TabList, TabPanel, TabPanels, Tabs, Tag, Text, Tooltip, VStack, useDisclosure, useToast
} from "@chakra-ui/react";
import { FaArrowLeft, FaUndo } from "react-icons/fa";
import { CalendarBlank, CaretLeft, CircleHalf, Clock, Flame, Lightning, ListBullets, Notepad, PencilSimple, TreeStructure, Truck, User } from "@phosphor-icons/react";
import { getFolderForQuestionnaire } from "../../api/common/fileinfo";
import fileinfoService from "../../api/common/fileinfo.service";
import { useTranslation } from "react-i18next";
import { DirectEmissions } from "./components/DirectEmissions";
import { CategoryQuestionnaire } from "./components/Categories";
import { IndirectEmissions } from "./components/IndirectEmissions";
import { useAuth } from "../../context";
import {
  directEmissionsKeys,
  indirectEmissionsKeys,
  organizationQuestionnaireKeys,
  useDefaultOrganizationQuestionnaireConfig,
  useDownloadScopeOneTemplate,
  useDownloadScopeTwoTemplate,
  useFinalizeQuestionnaire,
  useGetDirectEmissionsItems,
  useGetIndirectEmissionsItems,
  useGetOnsiteGeneration,
  useGetPurchasedElectricity,
  useGetOrganizationStructure,
  useGetQuestionnaireById,
  useGetQuestionnaireDataById,
  useImportScopeOneAndTwoData,
  useUnfinaliseOrgQuestionnaire,
  useUpdateMethodology,
  useUpdateQuestionnaireFiles,
  useUpdateScopeTwoVersion
} from "../../hooks/query";
import { dimensionsKeys, isScopeThreeVisible, QUESTIONNAIRE_STATUSES, showErrorToast, showSuccessToast } from "../../utils";
import { useDispatch, useSelector } from "react-redux";
import { hideAppSpinner, showAppSpinner } from "../../context/Store/slices/appSpinnerSlice";
import { useQueryClient } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { routes } from "../../config";
import { useRightSidebar } from "../../context/RightSidebarContext";
import { getTabIndexes, TAB_TYPES } from "./helpers";
import { CATEGORIES_KEYS, CATEGORIES_STEPS, CATEGORY_KEY_TO_SCOPE_ENUM } from "./components/Categories/const";
import { H2 } from "../../components/text/headings";
import stepRegistry from "./components/Categories/stepRegistry";
import { CHANGE_ACTIONS } from "./components/Categories/components/Steps/TabStepsPanels";
import { setAction } from "../../context/Store/slices/questionnaireSlice";
import { ConfirmationDialog } from "../../components/ConfirmationDialog";
import { EmissionsDataGrid } from "./components/DimensionsGrid";
import MethodologyAndSources from "./components/MethodologyAndSources";
import { useGetManualEmissionItems } from "../../hooks/query/questionnaire/manual-emission-item";
import { useGetBusinessTravelItems } from "../../hooks/query/questionnaire/categories/business-travel";
import { useGetEmployeeCommutingItems } from "../../hooks/query/questionnaire/categories/employee-commuting";
import { useGetCapitalGoodsItems } from "../../hooks/query/questionnaire/categories/capitalGoods";
import { useGetPurchasedProductItems } from "../../hooks/query/questionnaire/categories/purchasedGoodsAndServices";
import { useGetPurchasedGoodsWaterUsageItems } from "../../hooks/query/questionnaire/categories/purchasedGoodsWaterUsage";
import { useGetUpstreamTransportationAndDistributionItems } from "../../hooks/query/questionnaire/categories/upstream-transportation-and-distribution";
import { useGetDownstreamTransportationAndDistributionItems } from "../../hooks/query/questionnaire/categories/downstream-transportation-and-distribution";
import { useGetWasteGeneratedInOperationsItems } from "../../hooks/query/questionnaire/categories/wasteGeneratedInOperations";
import { useGetWasteWastewaterItems } from "../../hooks/query/questionnaire/categories/wasteWastewater";
import { useGetTreatmentOfSoldProductsItems } from "../../hooks/query/questionnaire/categories/treatment-of-sold-products";
import { useGetUseOfSoldProductsItems } from "../../hooks/query/questionnaire/categories/useOfSoldProducts";

// Mirrors STATUS_CONFIG in DataEntryPlanning/components/OrganizationPlanning/components/TaskCard.jsx
// so the status bar reads the same in both places.
const STATUS_CONFIG = {
  [QUESTIONNAIRE_STATUSES.NEW]: { progress: 15, color: "#FC8181", label: "No data" },
  [QUESTIONNAIRE_STATUSES.IN_PROGRESS]: { progress: 50, color: "#FBD38D", label: "In Progress" },
  [QUESTIONNAIRE_STATUSES.FINALIZED]: { progress: 100, color: "#B8E086", label: "Finalized" },
};

// Same icon + statusTitle label styling as the ISSB Manage panel, but with
// the value placed to the right rather than stacked below — keeps the
// existing single-row layout while picking up the larger icons / heavier
// label styling.
const ManageInfoRow = ({ icon, label, children }) => (
  <Flex gap={3} align="flex-start">
    <Flex align="center" gap={2} minW="120px" pt={0.5}>
      {icon}
      <Text variant="statusTitle">{label}</Text>
    </Flex>
    <Box flex={1}>{children}</Box>
  </Flex>
);

// Mirrors SCOPE_COLORS in DataEntryPlanning/components/OrganizationPlanning/components/TaskCard.jsx
// so the chips look the same in both places. Update here AND there if you change them.
const SCOPE_TAG_COLORS = {
  scope1: { light: "#E8FAF0", text: "#0B5E30", icon: Flame },
  scope2: { light: "#E8F4FD", text: "#1A5276", icon: Lightning },
  scope3: { light: "#F3E8FE", text: "#5B1D99", icon: Truck },
};

const QuestionnaireManagePanel = ({ questionnaireData, organizationStructure, onUnfinalise, isAdmin, isESGAdmin, onChangeScopeTwoVersion }) => {
  const navigate = useNavigate();
  const { t } = useTranslation("translation");
  const statusConfig = STATUS_CONFIG[questionnaireData?.status] || { progress: 0, color: "#E2E8F0", label: questionnaireData?.status };
  const scopeTags = [];
  if (questionnaireData?.configurations) {
    const configs = questionnaireData.configurations;
    if (configs.directEmissionsShouldBeFilled) scopeTags.push({ label: "Scope 1", ...SCOPE_TAG_COLORS.scope1 });
    if (configs.indirectEmissionsShouldBeFilled) scopeTags.push({ label: "Scope 2", ...SCOPE_TAG_COLORS.scope2 });
    const cats = [];
    Object.keys(configs).filter(k => configs[k] && k.endsWith("ShouldBeFilled") && k !== "directEmissionsShouldBeFilled" && k !== "indirectEmissionsShouldBeFilled").forEach(key => {
      const name = key.substring(0, key.indexOf("ShouldBeFilled"));
      const idx = CATEGORIES_STEPS.indexOf(name);
      if (idx >= 0) cats.push(`Cat ${idx + 1}`);
    });
    if (cats.length > 0) scopeTags.push({ label: "Scope 3", extra: cats.join(", "), ...SCOPE_TAG_COLORS.scope3 });
  }

  const dimensions = [];
  if (organizationStructure?.dimensions && questionnaireData) {
    const dimItemKeyMap = {
      dimensionOne: "dimensionOneItem",
      dimensionTwo: "dimensionTwoItem",
      dimensionThree: "dimensionThreeItem",
      dimensionFour: "dimensionFourItem",
      dimensionFive: "dimensionFiveItem",
    };
    const dimItems = questionnaireData.dimensionItems || questionnaireData;
    dimensionsKeys.forEach((dimKey) => {
      const dim = organizationStructure.dimensions[dimKey];
      if (dim) {
        const itemId = dimItems[dimItemKeyMap[dimKey]];
        const item = itemId ? dim.items?.find((i) => i.id === itemId) : null;
        dimensions.push({ name: dim.name, value: item ? item.name : "All" });
      }
    });
  }

  return (
    <Tabs variant="primary" defaultIndex={0} isLazy>
      <TabList position="sticky" top="0" bg="gray.50" zIndex="10" px={4}>
        <Tab>Manage</Tab>
      </TabList>
      <TabPanels>
        <TabPanel p={4}>
          <VStack align="stretch" spacing={6}>
            {/* Status — same label + progress bar pattern as the data tasks list */}
            <ManageInfoRow icon={<CircleHalf size={18} color="#A0AEC0" />} label="Status">
              <Box>
                <Text fontSize="xs" fontWeight="600" color="gray.600" mb={1}>
                  {statusConfig.label}
                </Text>
                <Box h="10px" borderRadius="full" bg="gray.100" overflow="hidden" w="75%">
                  <Box h="100%" w={`${statusConfig.progress}%`} borderRadius="full" bg={statusConfig.color} />
                </Box>
              </Box>
            </ManageInfoRow>

            {/* Assignee */}
            <ManageInfoRow icon={<User size={18} color="#A0AEC0" />} label="Assignee">
              <Box display="inline-block" bg="#FFF8E1" borderRadius="md" px={2} py={0.5}>
                <Text fontSize="11px" fontWeight="600" color="gray.700" textTransform="uppercase">
                  {questionnaireData?.assigneeName}
                </Text>
              </Box>
            </ManageInfoRow>

            {/* Period */}
            <ManageInfoRow icon={<CalendarBlank size={18} color="#A0AEC0" />} label="Period">
              <Text fontSize="14px" color="gray.700">
                {questionnaireData?.period?.year}{questionnaireData?.period?.quarter ? ` - ${questionnaireData.period.quarter}` : ""}
              </Text>
            </ManageInfoRow>

            {/* Scopes */}
            {scopeTags.length > 0 && (
              <ManageInfoRow icon={<ListBullets size={18} color="#A0AEC0" />} label="Scopes">
                <Flex gap={1.5} flexWrap="wrap">
                  {scopeTags.map((scope) => {
                    const ScopeIcon = scope.icon;
                    return (
                      <HStack key={scope.label} spacing={0}>
                        <Tag size="sm" bg={scope.light} color={scope.text} fontSize="11px" fontWeight="600" borderRadius="full" px={2} gap={1}
                          borderRightRadius={scope.extra ? 0 : "full"}
                        >
                          {ScopeIcon && <ScopeIcon size={12} weight="fill" />}
                          {scope.label}
                        </Tag>
                        {scope.extra && (
                          <Tag size="sm" bg={scope.light} color={scope.text} fontSize="11px" fontWeight="400" borderRadius="full" borderLeftRadius={0} px={2} opacity={0.8}>
                            · {scope.extra}
                          </Tag>
                        )}
                      </HStack>
                    );
                  })}
                </Flex>
              </ManageInfoRow>
            )}

            {/* Dimensions */}
            {dimensions.map((dim, idx) => (
              <ManageInfoRow key={idx} icon={<TreeStructure size={18} color="#A0AEC0" />} label={dim.name}>
                <Text fontSize="14px" color="gray.700">{dim.value}</Text>
              </ManageInfoRow>
            ))}

            {/* Last Edited */}
            {questionnaireData?.lastModifiedBy && (
              <ManageInfoRow icon={<PencilSimple size={18} color="#A0AEC0" />} label="Last Edit">
                <Box display="inline-block" bg="#FFF8E1" borderRadius="md" px={2} py={0.5}>
                  <Text fontSize="11px" fontWeight="600" color="gray.700" textTransform="uppercase">
                    {questionnaireData.lastModifiedBy}
                  </Text>
                </Box>
              </ManageInfoRow>
            )}
            {questionnaireData?.lastModifiedDate && (
              <ManageInfoRow icon={<Clock size={18} color="#A0AEC0" />} label="Edit Date">
                <Text fontSize="14px" color="gray.700">
                  {new Date(questionnaireData.lastModifiedDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  {" "}
                  <Text as="span" color="gray.500" fontSize="12px">
                    {new Date(questionnaireData.lastModifiedDate).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                  </Text>
                </Text>
              </ManageInfoRow>
            )}

            {/* Edit Task */}
            {questionnaireData?.id && questionnaireData?.status !== QUESTIONNAIRE_STATUSES.FINALIZED && (
              <Button
                size="sm"
                variant="outline"
                borderColor="gray.300"
                color="gray.700"
                leftIcon={<PencilSimple size={14} />}
                onClick={() => navigate(`${routes.dataCollectionNewTask}?id=${questionnaireData.id}&from=task`)}
                mt={2}
                _hover={{ bg: "gray.50" }}
              >
                Edit Task
              </Button>
            )}

            {/* Unfinalise Task (admin + finalized only) */}
            {questionnaireData?.id && questionnaireData?.status === QUESTIONNAIRE_STATUSES.FINALIZED && isAdmin && (
              <Button
                size="sm"
                variant="outline"
                borderColor="gray.300"
                color="gray.700"
                leftIcon={<FaUndo opacity="0.7" />}
                onClick={() => onUnfinalise?.(questionnaireData.id)}
                mt={2}
                _hover={{ bg: "gray.50" }}
              >
                Unfinalise Task
              </Button>
            )}

            {/* ESG-3927: ESG_ADMIN-only Scope 2 engine override (V1/V2) */}
            {questionnaireData?.id && isESGAdmin && (
              <Box mt={4}>
                <Text fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" mb={1}>
                  {t("organizationQuestionnaire.scope2V2.engineToggle.label")}
                </Text>
                <Select
                  size="sm"
                  borderRadius="md"
                  value={questionnaireData.scopeTwoVersion
                    ?? organizationStructure?.scopeTwoVersion
                    ?? "V1"}
                  onChange={(e) => onChangeScopeTwoVersion?.(questionnaireData.id, e.target.value)}
                  _hover={{ borderColor: "gray.400" }}
                  _focus={{ borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" }}
                  _focusVisible={{ borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" }}
                >
                  <option value="V1">{t("organizationQuestionnaire.scope2V2.engineToggle.v1")}</option>
                  <option value="V2">{t("organizationQuestionnaire.scope2V2.engineToggle.v2")}</option>
                </Select>
              </Box>
            )}
          </VStack>
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

const DATA_TYPES = {
  ACTIVITY: "activity",
  EMISSIONS: "emissions",
};

const DataTypeSelector = ({ selected, onChange, config }) => {
  const activityEnabled = config?.activityDataEnabled ?? true;
  const emissionsEnabled = config?.emissionsDataEnabled ?? true;

  // Don't show selector if only one option is available
  if (!activityEnabled && !emissionsEnabled) return null;
  if (activityEnabled && !emissionsEnabled) return null;
  if (!activityEnabled && emissionsEnabled) return null;

  const options = [
    { key: DATA_TYPES.ACTIVITY, label: "Add Activity Data", description: "Enter raw operational data (e.g. fuel, electricity usage)" },
    { key: DATA_TYPES.EMISSIONS, label: "Add Emissions Data", description: "Enter pre-calculated emissions data (e.g. tCO\u2082e)" },
  ];
  return (
    <Flex gap={3} mb={4} maxW="560px">
      {options.map((opt) => {
        const isSelected = selected === opt.key;
        return (
          <Flex
            key={opt.key}
            flex={1}
            p={4}
            gap={3}
            align="flex-start"
            border="2px solid"
            borderColor={isSelected ? "lime" : "gray.200"}
            bg={isSelected ? "lightLime" : "white"}
            borderRadius="lg"
            cursor="pointer"
            onClick={() => onChange(opt.key)}
            transition="all 0.15s"
            _hover={{ borderColor: "lime" }}
          >
            <Radio
              isChecked={isSelected}
              colorScheme="limeShade"
              mt={1}
              pointerEvents="none"
            />
            <Box>
              <Text fontSize="16px" fontWeight="700" color={isSelected ? "gray.800" : "gray.600"}>{opt.label}</Text>
              <Text fontSize="12px" color="gray.400" lineHeight="1.3" mt={1}>{opt.description}</Text>
            </Box>
          </Flex>
        );
      })}
    </Flex>
  );
};

// Segmented-control toggle for the data-input mode (activity vs pre-calculated
// emissions). Visually distinct from the outer Scope tabs \u2014 pill-shaped,
// inline with a "Data input mode" label \u2014 so users don't read it as a second
// row of scope tabs. A lime dot per segment shows whether data already exists
// on that side, making partly-filled tasks obvious on entry.
const DataTypeTabs = ({ selected, onChange, config, hasActivityData, hasEmissionsData }) => {
  const activityEnabled = config?.activityDataEnabled ?? true;
  const emissionsEnabled = config?.emissionsDataEnabled ?? true;

  if (!activityEnabled || !emissionsEnabled) return null;

  const tabs = [
    {
      key: DATA_TYPES.ACTIVITY,
      label: "Activity Data",
      hint: "Raw operational data (e.g. fuel, electricity usage). This data will be used to automatically calculate emissions.",
      hasData: hasActivityData,
    },
    {
      key: DATA_TYPES.EMISSIONS,
      label: "Emissions Data",
      hint: "Pre-calculated emissions data (e.g. tCO\u2082e). This will simply be added to your total for this scope.",
      hasData: hasEmissionsData,
    },
  ];

  const activeHint = tabs.find((t) => t.key === selected)?.hint;

  return (
    <Box mb={4}>
      <Flex align="center" gap={3} flexWrap="wrap">
        <Text fontSize="12px" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.04em">
          Data input mode
        </Text>
        <Flex
          role="tablist"
          bg="gray.100"
          borderRadius="full"
          p="3px"
          gap="2px"
        >
          {tabs.map((tab) => {
            const isSelected = selected === tab.key;
            return (
              <Box
                key={tab.key}
                role="tab"
                aria-selected={isSelected}
                tabIndex={0}
                onClick={() => onChange(tab.key)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onChange(tab.key); } }}
                cursor="pointer"
                px={4}
                py={1.5}
                borderRadius="full"
                bg={isSelected ? "white" : "transparent"}
                boxShadow={isSelected ? "0 1px 2px rgba(0,0,0,0.06)" : "none"}
                transition="background 0.15s, box-shadow 0.15s"
                _hover={isSelected ? {} : { bg: "gray.200" }}
              >
                <Flex align="center" gap={2}>
                  <Text
                    fontSize="13px"
                    fontWeight={isSelected ? "700" : "500"}
                    color={isSelected ? "gray.800" : "gray.500"}
                  >
                    {tab.label}
                  </Text>
                  {tab.hasData && (
                    <Box
                      as="span"
                      w="6px"
                      h="6px"
                      borderRadius="full"
                      bg="lime"
                      title="This section has data"
                      aria-label="This section has data"
                    />
                  )}
                </Flex>
              </Box>
            );
          })}
        </Flex>
        {activeHint && (
          <Text fontSize="12px" color="gray.400">{activeHint}</Text>
        )}
      </Flex>
    </Box>
  );
};

// Tab label paired with a small lime dot when the underlying scope/category
// has any data. Keeps the tab compact + readable when many tabs share the
// same row.
const TabLabelWithDot = ({ label, hasData }) => (
  <Flex align="center" gap={1.5}>
    <Box as="span">{label}</Box>
    {hasData && (
      <Box
        as="span"
        w="6px"
        h="6px"
        borderRadius="full"
        bg="lime"
        flexShrink={0}
        title="This tab has data"
        aria-label="This tab has data"
      />
    )}
  </Flex>
);

const getActiveScope3Categories = (configurations) => {
  const active = Object.keys(configurations)
    .filter(key => key.endsWith("ShouldBeFilled") && configurations[key])
    .filter(key => key !== "directEmissionsShouldBeFilled" && key !== "indirectEmissionsShouldBeFilled")
    .map(key => key.substring(0, key.indexOf("ShouldBeFilled")));
  return CATEGORIES_STEPS.filter(step => active.includes(step));
};

// Maps a Scope 3 category key to its activity-data items hook, used to drive
// the "has data" indicator on the Activity tab. Categories without their own
// activity-data table (e.g. franchises, investments, fuel & energy related
// activities) are intentionally absent; for those we fall back to checking
// only manual emissions.
const SCOPE3_ACTIVITY_HOOKS = {
  businessTravel: useGetBusinessTravelItems,
  employeeCommuting: useGetEmployeeCommutingItems,
  capitalGoods: useGetCapitalGoodsItems,
  purchasedGoodsAndServices: useGetPurchasedProductItems,
  upstreamTransportationAndDistribution: useGetUpstreamTransportationAndDistributionItems,
  downstreamTransportationAndDistribution: useGetDownstreamTransportationAndDistributionItems,
  wasteGeneratedInOperations: useGetWasteGeneratedInOperationsItems,
  useOfSoldProducts: useGetUseOfSoldProductsItems,
  treatmentOfSoldProducts: useGetTreatmentOfSoldProductsItems,
};

// Categories where activity data isn't a meaningful workflow. Force emissions-data
// UI regardless of the task's activity/emissions config so the user lands directly
// on the EmissionsDataGrid. Cat 3 is here because its tables are derived from
// Scope 1+2 server-side — users only enter manual emissions on this page.
const EMISSIONS_ONLY_CATEGORIES = new Set([
  CATEGORIES_KEYS.franchises,
  CATEGORIES_KEYS.investments,
  CATEGORIES_KEYS.fuelAndEnergyRelatedActivities,
]);

// Encapsulates the per-category activity + manual-emissions queries so the
// data-type tabs can show a "has data" dot from first render. Hooks are
// resolved by category key, but `useScope3CategoryHasData` itself always
// calls both queries (potentially with no-op hook) to keep React's hook
// rule of stable call order.
const NOOP_HOOK = () => ({ data: undefined });
const useScope3CategoryHasData = (catKey, questionnaireId, enabled) => {
  const ActivityHook = SCOPE3_ACTIVITY_HOOKS[catKey] || NOOP_HOOK;
  const scopeCategoryKey = CATEGORY_KEY_TO_SCOPE_ENUM[catKey];
  const { data: activityPage } = ActivityHook(
    { queryParams: { page: 0, size: 1, sort: "id,desc" }, questionnaireId },
    { enabled: enabled && !!questionnaireId && !!SCOPE3_ACTIVITY_HOOKS[catKey], cacheTime: 0, refetchOnWindowFocus: false }
  );
  const { data: manualEmissions = [] } = useGetManualEmissionItems(
    { questionnaireId, scopeCategoryKey },
    { enabled: enabled && !!questionnaireId && !!scopeCategoryKey }
  );
  return {
    hasActivityData: (activityPage?.totalElements ?? activityPage?.content?.length ?? 0) > 0,
    hasEmissionsData: manualEmissions.length > 0,
  };
};

const Scope3CategoryPanel = ({
  catKey,
  defaultValues,
  onTabChange,
  returnToListRequested,
  returnToList,
  declineReturnToListRequest,
  allowedToSubmit,
  isPreview,
  canFinalize,
  onHasData,
  indexes,
  dataType,
  setDataType,
  organizationStructure,
  methodologyAndSourcesPanel,
  onSaveAndClose,
  configurations,
  showDataTypeTabs,
}) => {
  const { t } = useTranslation("translation");
  const [finalize, setFinalize] = useState(false);
  const { isOpen: isConfirmDialogOpen, onOpen: onConfirmDialogOpen, onClose: onConfirmDialogClose } = useDisclosure();
  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const saveBtnRef = useRef();
  const action = useSelector(state => state.questionnaire.action);

  useEffect(() => {
    if (onHasData && isPreview) onHasData(true);
  }, [onHasData, isPreview]);

  useEffect(() => {
    if (allowedToSubmit && returnToListRequested) {
      saveBtnRef.current?.click();
    }
  }, [allowedToSubmit, returnToListRequested]);

  const { mutate: finalizeQuestionnaire } = useFinalizeQuestionnaire({
    ...useDefaultOrganizationQuestionnaireConfig(),
    onSuccess: () => {
      showSuccessToast(toast, t("organizationQuestionnaire.page.finalized"));
      queryClient.invalidateQueries(organizationQuestionnaireKeys.orgQuestionnaireById());
      returnToList();
    },
    onError: err => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
      setFinalize(false);
    }
  });

  const handleCategoryChange = React.useCallback(() => {
    if (action === CHANGE_ACTIONS.PREV_SECTION) {
      const prevIndex = indexes[TAB_TYPES.INDIRECT_EMISSIONS] ?? indexes[TAB_TYPES.DIRECT_EMISSIONS];
      if (prevIndex !== undefined) {
        onTabChange(prevIndex);
      }
    }
    if (returnToListRequested) {
      returnToList();
    }
  }, [action, onTabChange, returnToList, returnToListRequested, indexes]);

  const handleFinalizeConfirmation = () => {
    const currentForm = document.getElementById(`formStep${catKey}`);
    if (currentForm) {
      currentForm.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
      handleDialogClose(true);
    }
  };

  const handleDialogClose = (confirmed) => {
    if (confirmed !== true) setFinalize(false);
    onConfirmDialogClose();
  };

  const { dimensionItems } = defaultValues;
  const isEmissionsOnly = EMISSIONS_ONLY_CATEGORIES.has(catKey);
  // Cat 3 is emissions-only and intentionally has no FormComponent registered.
  const FormComponent = isEmissionsOnly && !stepRegistry.has(catKey) ? null : stepRegistry.resolve(catKey);
  const CustomHeaderComponent = !isPreview ? stepRegistry.resolveCustomHeader(catKey) : null;
  const customHeaderProps = stepRegistry.resolveCustomHeaderProps(catKey);

  const scopeCategoryKey = CATEGORY_KEY_TO_SCOPE_ENUM[catKey];
  const { data: manualEmissionItems = [] } = useGetManualEmissionItems(
    { questionnaireId: defaultValues?.id, scopeCategoryKey },
    { enabled: isPreview && !!defaultValues?.id && !!scopeCategoryKey }
  );
  const hasManualEmissions = manualEmissionItems.length > 0;

  // Drive the Activity / Emissions data-type tabs. Run independently of the
  // preview-only manual-emissions query above so the indicator dots are
  // accurate while editing too.
  const { hasActivityData, hasEmissionsData } = useScope3CategoryHasData(
    catKey,
    defaultValues?.id,
    !isPreview && showDataTypeTabs
  );

  const effectiveDataType = isEmissionsOnly ? "emissions" : dataType;
  const showActivityForm = !isPreview ? effectiveDataType !== "emissions" : !isEmissionsOnly;
  const showManualEmissionsGrid = !isPreview ? effectiveDataType === "emissions" : (isEmissionsOnly || hasManualEmissions);

  return (
    <Box>
      {showDataTypeTabs && !isPreview && !isEmissionsOnly && (
        <DataTypeTabs
          selected={dataType}
          onChange={(type) => setDataType?.(type)}
          config={configurations}
          hasActivityData={hasActivityData}
          hasEmissionsData={hasEmissionsData}
        />
      )}
      {showManualEmissionsGrid && (
        <Box
          as="form"
          id={!showActivityForm ? `formStep${catKey}` : undefined}
          onSubmit={(e) => {
            e.preventDefault();
            if (finalize) {
              finalizeQuestionnaire(defaultValues.id);
            } else {
              handleCategoryChange();
            }
          }}
          mt={4}
        >
          <EmissionsDataGrid
            organizationStructure={organizationStructure}
            dimensionItems={dimensionItems}
            isPreview={isPreview}
            questionnaireId={defaultValues?.id}
            scopeCategoryKey={scopeCategoryKey}
          />
        </Box>
      )}
      {showActivityForm && (
        <>
          {CustomHeaderComponent && (
            <Flex justifyContent="flex-end" mb={2}>
              <CustomHeaderComponent questionnaireId={defaultValues?.id} {...customHeaderProps} />
            </Flex>
          )}
          <FormComponent
            tabKey={catKey}
            defaultValues={defaultValues}
            handleCategoryChange={handleCategoryChange}
            finalize={finalize}
            finalizeQuestionnaire={finalizeQuestionnaire}
            setFinalize={setFinalize}
            declineReturnToListRequest={declineReturnToListRequest}
            isPreview={isPreview}
            organizationStructure={organizationStructure}
            dimensionItems={dimensionItems}
          />
        </>
      )}
      {methodologyAndSourcesPanel}
      {!isPreview && (
        <Flex
          data-sticky-footer
          direction="row"
          justify="space-between"
          gap={4}
          mt={8}
          position="sticky"
          bottom="0"
          bg="white"
          borderTop="1px solid"
          borderColor="gray.200"
          zIndex="10"
          py={4}
        >
          {/* The hidden submit-button ref is still needed: the existing
              "save then return" flow (initiated from the breadcrumb /
              Save & close) clicks it programmatically so any in-progress
              row gets committed before navigation. */}
          <Button
            form={`formStep${catKey}`}
            type="submit"
            ref={saveBtnRef}
            display="none"
            aria-hidden
          />
          <Button
            flex={1}
            bg="limeShade.100"
            color="pureBlack"
            _hover={{ bg: "limeShade.200" }}
            onClick={onSaveAndClose}
          >
            {t("buttons.saveAndClose")}
          </Button>
          <Tooltip label={canFinalize ? "" : "Add data, methodology, or a file before finalising"} placement="top" hasArrow>
            <Button
              flex={1}
              bg="lime"
              color="pureBlack"
              _hover={{ bg: "limeShade.300" }}
              isDisabled={!canFinalize}
              onClick={() => {
                setFinalize(true);
                onConfirmDialogOpen();
              }}
            >
              {t("buttons.finalize")}
            </Button>
          </Tooltip>
        </Flex>
      )}
      <ConfirmationDialog
        isOpen={isConfirmDialogOpen}
        onClose={handleDialogClose}
        onConfirm={handleFinalizeConfirmation}
        headerText={t("questionnaireFinalizationDialog.header")}
        confirmationText={t("questionnaireFinalizationDialog.question")}
      />
    </Box>
  );
};

function OrganizationQuestionnaire({ questionnaireId, isOpenedInModal, viewOnly = false }) {
  const headingRef = useRef(null);
  const tabListRef = useRef(null);
  const { t: globalTranslation } = useTranslation("translation");
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire"
  });
  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    const list = tabListRef.current;
    if (!list) return;
    const selected = list.querySelector('[aria-selected="true"]');
    if (!selected) return;
    // Scroll only the TabList itself; scrollIntoView walks up the DOM
    // and would also shift any scrollable ancestor (incl. the page).
    const targetLeft = selected.offsetLeft - (list.clientWidth - selected.offsetWidth) / 2;
    list.scrollTo({ left: Math.max(0, targetLeft), behavior: "smooth" });
  }, [tabIndex]);

  const [scopeHasData, setScopeHasData] = useState({});
  const handleScopeHasData = React.useCallback((scope, hasData) => {
    setScopeHasData(prev => {
      if (prev[scope] === hasData) return prev;
      return { ...prev, [scope]: hasData };
    });
  }, []);
  const { user, userPermissions } = useAuth();
  const isAdmin = userPermissions?.includes("ORG_ADMIN");
  const isESGAdmin = userPermissions?.includes("ESG_ADMIN");
  const navigate = useNavigate();
  const { setRightSidebar, doOpen, doClose, isOpen, activationId, setTopOffset } = useRightSidebar();
  useEffect(() => headingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), [tabIndex]);
  const assignee = {
    name: user.name,
    avatarUrl: user.picture
  };
  // These tab indexes are introduced due to the variable set of sections of each individual questionnaire
  const [indexes, setIndexes] = useState({});

  const [questionnaireData, setQuestionnaireData] = useState();
  const [submitAndReturnToList, setSubmitAndReturnToList] = useState(false);
  const [dataTypes, setDataTypes] = useState({ scope1: DATA_TYPES.ACTIVITY, scope2: DATA_TYPES.ACTIVITY, scope3: DATA_TYPES.ACTIVITY });
  const [filesByScope, setFilesByScope] = useState({});
  // Local mirror of methodology by scope so removals (even before the server
  // round-trip completes) reflect immediately in the Finalize gate.
  const [methodologyByScope, setMethodologyByScope] = useState({});

  const {
    data: organizationStructure
  } = useGetOrganizationStructure();

  // Scope 1 has-data flags for the Activity / Emissions tabs. Run these at
  // the parent level so the indicator dot is correct on first render and
  // doesn't rely on the inner DimensionsGrid mounting (which only happens
  // for whichever data type is currently selected).
  const scope1Enabled = !!questionnaireData?.id && !!questionnaireData?.configurations?.directEmissionsShouldBeFilled;
  const scope1ItemQueryOpts = {
    enabled: scope1Enabled,
    cacheTime: 0,
    refetchOnWindowFocus: false,
  };
  const sharedDirectQueryParams = { queryParams: { page: 0, size: 1, sort: "id,desc" }, questionnaireId: questionnaireData?.id };
  const { data: scope1StationaryPage } = useGetDirectEmissionsItems({ ...sharedDirectQueryParams, type: "stationary" }, scope1ItemQueryOpts);
  const { data: scope1MobilePage } = useGetDirectEmissionsItems({ ...sharedDirectQueryParams, type: "mobile" }, scope1ItemQueryOpts);
  const { data: scope1RefrigerantPage } = useGetDirectEmissionsItems({ ...sharedDirectQueryParams, type: "refrigerant" }, scope1ItemQueryOpts);
  const scope1HasActivityData = (scope1StationaryPage?.totalElements ?? 0) > 0
    || (scope1MobilePage?.totalElements ?? 0) > 0
    || (scope1RefrigerantPage?.totalElements ?? 0) > 0;
  const { data: scope1ManualEmissions = [] } = useGetManualEmissionItems(
    { questionnaireId: questionnaireData?.id, scopeCategoryKey: "SCOPE_1" },
    { enabled: scope1Enabled }
  );
  const scope1HasEmissionsData = scope1ManualEmissions.length > 0;

  // Scope 2 has-data flags — same pattern as Scope 1, but indirect emissions
  // only has the single "electricity" type so this is one query rather than
  // three.
  const scope2Enabled = !!questionnaireData?.id && !!questionnaireData?.configurations?.indirectEmissionsShouldBeFilled;
  const scope2QueryOpts = { enabled: scope2Enabled, cacheTime: 0, refetchOnWindowFocus: false };
  const { data: scope2ElectricityPage } = useGetIndirectEmissionsItems(
    { queryParams: { page: 0, size: 1, sort: "id,desc" }, type: "electricity", questionnaireId: questionnaireData?.id },
    scope2QueryOpts
  );
  // ESG-3927: the green dot must also reflect Scope 2 v2 data (purchased +
  // on-site), not just the v1 electricity grid — else a V2 questionnaire shows
  // no indicator despite having data.
  const { data: scope2PurchasedPage } = useGetPurchasedElectricity(
    { questionnaireId: questionnaireData?.id, queryParams: { page: 0, size: 1, sort: "id,desc" } },
    scope2QueryOpts
  );
  const { data: scope2OnsitePage } = useGetOnsiteGeneration(
    { questionnaireId: questionnaireData?.id, queryParams: { page: 0, size: 1, sort: "id,desc" } },
    scope2QueryOpts
  );
  const scope2HasActivityData = (scope2ElectricityPage?.totalElements ?? 0) > 0
    || (scope2PurchasedPage?.totalElements ?? 0) > 0
    || (scope2OnsitePage?.totalElements ?? 0) > 0;
  const { data: scope2ManualEmissions = [] } = useGetManualEmissionItems(
    { questionnaireId: questionnaireData?.id, scopeCategoryKey: "SCOPE_2" },
    { enabled: scope2Enabled }
  );
  const scope2HasEmissionsData = scope2ManualEmissions.length > 0;

  // Scope 3 has-data flags. Mount one hook per category at the parent so the
  // Finalize button updates the moment a row is added in any category, not
  // only after a refresh. The hook list is stable per render (driven by
  // SCOPE3_ACTIVITY_HOOKS keys), satisfying rules-of-hooks.
  const scope3Configs = questionnaireData?.configurations || {};
  const sharedScope3QueryParams = { queryParams: { page: 0, size: 1, sort: "id,desc" }, questionnaireId: questionnaireData?.id };
  const scope3CatEnabled = (catKey) => {
    if (!questionnaireData?.id) return false;
    return !!scope3Configs[`${catKey}ShouldBeFilled`];
  };

  // Drive the per-category green dots and the parent Scope 3 dot from a single
  // pass over CATEGORIES_STEPS. useScope3CategoryHasData covers both activity
  // rows and manual emissions for every category, so emissions-only ones
  // (cats 3, 14, 15) and previously-unwired ones (cats 8, 10, 11, 13) light up
  // the moment a row is added.
  const scope3CatHasData = CATEGORIES_STEPS.reduce((acc, catKey) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { hasActivityData, hasEmissionsData } = useScope3CategoryHasData(
      catKey,
      questionnaireData?.id,
      scope3CatEnabled(catKey)
    );
    acc[catKey] = hasActivityData || hasEmissionsData;
    return acc;
  }, {});

  // Cat 1 water usage (ESG-3900) and Cat 5 wastewater (ESG-3924) are side-section
  // tables outside SCOPE3_ACTIVITY_HOOKS; OR their rows into the parent category.
  const sharedScope3LiveOpts = { enabled: !!questionnaireData?.id, cacheTime: 0, refetchOnWindowFocus: false };
  const { data: purchasedGoodsWaterPage } = useGetPurchasedGoodsWaterUsageItems(
    sharedScope3QueryParams,
    { ...sharedScope3LiveOpts, enabled: sharedScope3LiveOpts.enabled && scope3CatEnabled("purchasedGoodsAndServices") }
  );
  const { data: wastewaterPage } = useGetWasteWastewaterItems(
    sharedScope3QueryParams,
    { ...sharedScope3LiveOpts, enabled: sharedScope3LiveOpts.enabled && scope3CatEnabled("wasteGeneratedInOperations") }
  );
  const pageHasRows = (page) => (page?.totalElements ?? page?.content?.length ?? 0) > 0;
  scope3CatHasData.purchasedGoodsAndServices = scope3CatHasData.purchasedGoodsAndServices || pageHasRows(purchasedGoodsWaterPage);
  scope3CatHasData.wasteGeneratedInOperations = scope3CatHasData.wasteGeneratedInOperations || pageHasRows(wastewaterPage);

  const scope3HasAnyData = Object.values(scope3CatHasData).some(Boolean);

  // Finalize is only allowed once SOMETHING has been added to the task — any
  // activity/emissions data in any scope, any saved methodology with actual
  // text, or any currently-attached file. Methodology and files reflect the
  // *current* state — if the user adds and then removes content, we treat it
  // as never having been added. (Scope 3 manual-emissions per-category are
  // tracked by each category panel — once one fires, the questionnaire's
  // status flips off NEW server-side and we treat the task as having content.)
  const hasMeaningfulText = (raw) => {
    if (!raw || typeof raw !== "string") return false;
    // Strip HTML tags + whitespace so empty richtext (e.g. "<p></p>",
    // "<p><br></p>") doesn't count as content.
    return raw.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim().length > 0;
  };
  const hasAnyMethodology = (() => {
    // Merge server map with local edits — local takes precedence so cleared
    // methodology immediately disables Finalize.
    const merged = {
      ...(questionnaireData?.methodologyMap || {}),
      ...(methodologyByScope || {}),
    };
    if (Object.values(merged).some(hasMeaningfulText)) return true;
    return hasMeaningfulText(questionnaireData?.methodology);
  })();
  // Merge server filesMap with the live filesByScope state, with local edits
  // taking precedence. This way removals (even local-only optimistic ones)
  // disable Finalize immediately, and the server values cover scopes the user
  // hasn't visited yet.
  const hasAnyFiles = (() => {
    const merged = { ...(questionnaireData?.filesMap || {}) };
    Object.entries(filesByScope || {}).forEach(([scopeKey, list]) => {
      merged[scopeKey] = Array.isArray(list) ? list.map((f) => String(f.id)) : [];
    });
    return Object.values(merged).some((ids) => Array.isArray(ids) && ids.length > 0);
  })();
  const taskHasContent =
    scope1HasActivityData ||
    scope1HasEmissionsData ||
    scope2HasActivityData ||
    scope2HasEmissionsData ||
    scope3HasAnyData ||
    hasAnyMethodology ||
    hasAnyFiles ||
    questionnaireData?.status === QUESTIONNAIRE_STATUSES.IN_PROGRESS ||
    questionnaireData?.status === QUESTIONNAIRE_STATUSES.FINALIZED;

  const TRAY_ID = "TaskDetails";

  // Set top offset for secondary header height
  useEffect(() => {
    if (!isOpenedInModal) {
      setTopOffset(52);
      return () => setTopOffset(0);
    }
  }, [isOpenedInModal, setTopOffset]);

  const queryClientForUnfinalise = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOpen: isUnfinaliseConfirmOpen, onOpen: onUnfinaliseConfirmOpen, onClose: onUnfinaliseConfirmClose } = useDisclosure();
  const [pendingUnfinaliseId, setPendingUnfinaliseId] = useState(null);

  const { mutate: unfinaliseTaskMutation } = useUnfinaliseOrgQuestionnaire({
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: () => {
      showSuccessToast(toast, t("dataEntryPlanning.messages.unfinaliseSuccess"));
      queryClientForUnfinalise.invalidateQueries(organizationQuestionnaireKeys.orgQuestionnaireById());
      queryClientForUnfinalise.invalidateQueries(organizationQuestionnaireKeys.allDataEntryPlanningQuestionnaires());
      // Drop view=true from URL so the page reloads in editable state
      if (searchParams.get("view")) {
        const next = new URLSearchParams(searchParams);
        next.delete("view");
        setSearchParams(next);
      }
      dispatch(hideAppSpinner());
    },
    onError: (err) => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
      dispatch(hideAppSpinner());
    },
    onSettled: () => dispatch(hideAppSpinner()),
  });

  const handleUnfinaliseTask = (id) => {
    setPendingUnfinaliseId(id);
    onUnfinaliseConfirmOpen();
  };

  const handleUnfinaliseConfirm = () => {
    if (pendingUnfinaliseId) {
      unfinaliseTaskMutation(pendingUnfinaliseId);
    }
    setPendingUnfinaliseId(null);
    onUnfinaliseConfirmClose();
  };

  // ESG-3927: ESG_ADMIN-only override of the Scope 2 engine (V1/V2). Invalidate
  // the questionnaire query so the Scope 2 tab re-renders under the new engine.
  const { mutate: updateScopeTwoVersionMutation } = useUpdateScopeTwoVersion({
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: () => {
      showSuccessToast(toast, t("organizationQuestionnaire.scope2V2.engineToggle.updated"));
      queryClientForUnfinalise.invalidateQueries(organizationQuestionnaireKeys.orgQuestionnaireById());
      queryClientForUnfinalise.invalidateQueries(organizationQuestionnaireKeys.dataEntryPlanningQuestionnaireById());
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
  });

  const handleChangeScopeTwoVersion = (id, scopeTwoVersion) =>
    updateScopeTwoVersionMutation({ questionnaireId: id, scopeTwoVersion });

  const toggleTaskDetails = () => {
    if (TRAY_ID === activationId && isOpen) {
      doClose();
    } else {
      setRightSidebar(
        <QuestionnaireManagePanel
          questionnaireData={questionnaireData}
          organizationStructure={organizationStructure}
          isAdmin={isAdmin}
          isESGAdmin={isESGAdmin}
          onUnfinalise={handleUnfinaliseTask}
          onChangeScopeTwoVersion={handleChangeScopeTwoVersion}
        />
      );
      doOpen(TRAY_ID);
    }
  };

  const getDataConfig = {
    onSuccess: response => {
      setQuestionnaireData(response);
      setIndexes(getTabIndexes(response));
      const config = response?.configurations;
      const activityEnabled = config?.activityDataEnabled ?? true;
      const emissionsEnabled = config?.emissionsDataEnabled ?? true;
      const defaultType = !activityEnabled && emissionsEnabled ? DATA_TYPES.EMISSIONS : DATA_TYPES.ACTIVITY;
      setDataTypes({ scope1: defaultType, scope2: defaultType, scope3: defaultType });
    },
    onError: err => err.response?.status !== 404 ? showErrorToast(toast, err.message) : null,
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0,
    refetchOnWindowFocus: false
  };

  const {
    data: userRelatedData,
    isLoading: isUserRelatedDataLoading,
    isFetching: isUserRelatedDataFetching,
    error: userRelatedError
  } = useGetQuestionnaireDataById(questionnaireId ?? null, {
    ...getDataConfig,
    enabled: questionnaireId !== null && !isNaN(questionnaireId) && !isOpenedInModal
  });

  const {
    data: planningData,
    isLoading: isPlanningDataLoading,
    isFetching: isPlanningDataFetching,
    error: planningError
  } = useGetQuestionnaireById(questionnaireId ?? null, {
    ...getDataConfig,
    enabled: questionnaireId !== null && !isNaN(questionnaireId) && isOpenedInModal
  });

  const importDataConfig = {
    onSuccess: () => {
      showSuccessToast(toast, t("page.imported"));
      queryClient.invalidateQueries(directEmissionsKeys.items());
      queryClient.invalidateQueries(indirectEmissionsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ??
      err.response?.data?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  };

  const { mutate: importData } = useImportScopeOneAndTwoData(importDataConfig);

  // `importBtnId` is still accepted because the Scope 1/2 grids pass it, but it no longer names anything:
  // their hidden file inputs became ref-based and reset themselves via `e.target.value = null`, so the
  // getElementById lookup this used to do always returned null. (CategoryBusinessTravel has its own,
  // separate id-based mechanism with a real matching input — that one is untouched.)
  const handleDataImport = (e, importBtnId, groupType) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      const formData = new FormData();
      formData.append("file", e.target.files[0]);
      dispatch(showAppSpinner());
      importData({
        questionnaireId: questionnaireData?.id,
        data: formData,
        groupType
      });
    }
  };

  const executeNavigation = () => {
    navigate(routes.dataCollectionDataEntryPlanning);
  };

  const handleReturnToListClick = () => {
    if (questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED || tabIndex === indexes[TAB_TYPES.DIRECT_EMISSIONS] ||
      tabIndex === indexes[TAB_TYPES.INDIRECT_EMISSIONS]) {
      executeNavigation();
    } else {
      setSubmitAndReturnToList(true);
    }
  };

  const handleNavigationToList = () => {
    setSubmitAndReturnToList(false);
    executeNavigation();
  };

  const downloadTemplateConfig = {
    onMutate: () => dispatch(showAppSpinner()),
    onError: err => {
      const parsedErrorObj = JSON.parse(String.fromCharCode.apply(null, new Int8Array(err.response.data)));
      showErrorToast(toast, parsedErrorObj?.message ?? err.message);
    },
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0
  };

  const { mutate: downloadScopeOneTemplate } = useDownloadScopeOneTemplate(downloadTemplateConfig);
  const { mutate: downloadScopeTwoTemplate } = useDownloadScopeTwoTemplate(downloadTemplateConfig);

  useEffect(() => {
    const map = questionnaireData?.methodologyMap;
    if (map && typeof map === "object") {
      setMethodologyByScope((prev) => ({ ...map, ...prev }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireData?.id]);

  const { mutate: updateMethodology, isLoading: isMethodologySaving } = useUpdateMethodology({
    onSuccess: () => {
      showSuccessToast(toast, "Methodology saved");
      queryClient.invalidateQueries(organizationQuestionnaireKeys.orgQuestionnaireById());
    },
    onError: (err) => showErrorToast(toast, err.message),
  });

  const handleSaveMethodology = (scopeKey, content) => {
    setMethodologyByScope((prev) => ({ ...prev, [scopeKey]: content }));
    updateMethodology({
      questionnaireId: questionnaireData?.id,
      data: { scopeKey, methodology: content },
    });
  };

  const { mutate: updateQuestionnaireFiles } = useUpdateQuestionnaireFiles({
    // Re-fetch the questionnaire so the rendered Sources list reflects the
    // canonical server-side filesMap, not just our optimistic local state.
    // If the backend silently drops the write until finalise, the re-fetched
    // questionnaire will come back with the old filesMap and the UI will
    // self-correct — making the backend regression immediately visible.
    onSuccess: (data) => {
      // eslint-disable-next-line no-console
      console.debug("[updateQuestionnaireFiles] success", data);
      queryClient.invalidateQueries(organizationQuestionnaireKeys.orgQuestionnaireById());
      queryClient.invalidateQueries(organizationQuestionnaireKeys.dataEntryPlanningQuestionnaireById());
    },
    onError: (err) => {
      // eslint-disable-next-line no-console
      console.error("[updateQuestionnaireFiles] error", err);
      showErrorToast(toast, err.message);
    },
  });

  useEffect(() => {
    const filesMap = questionnaireData?.filesMap;
    if (!filesMap) return;
    const scopeKeys = Object.keys(filesMap);
    if (scopeKeys.length === 0) return;
    const pending = scopeKeys.filter((k) => !(k in filesByScope));
    if (pending.length === 0) return;
    pending.forEach(async (scopeKey) => {
      const ids = filesMap[scopeKey] || [];
      if (ids.length === 0) {
        setFilesByScope((prev) => ({ ...prev, [scopeKey]: [] }));
        return;
      }
      try {
        const fileInfos = await Promise.all(ids.map((id) => fileinfoService.get(id)));
        setFilesByScope((prev) => ({ ...prev, [scopeKey]: fileInfos.filter(Boolean) }));
      } catch (err) {
        showErrorToast(toast, err.message);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireData?.filesMap]);

  const handleFilesChange = (scopeKey, fileInfoList) => {
    setFilesByScope((prev) => ({ ...prev, [scopeKey]: fileInfoList }));
    updateQuestionnaireFiles({
      questionnaireId: questionnaireData?.id,
      data: { scopeKey, fileIds: fileInfoList.map((f) => String(f.id)) },
    });
  };

  const renderTabs = () => {
    const scope3Visible = isScopeThreeVisible(questionnaireData);
    const activeCategories = scope3Visible ? getActiveScope3Categories(questionnaireData.configurations) : [];
    const multiCatScope3 = activeCategories.length > 0;

    const tabCount = [
      questionnaireData.configurations.directEmissionsShouldBeFilled,
      questionnaireData.configurations.indirectEmissionsShouldBeFilled,
      scope3Visible && !multiCatScope3,
    ].filter(Boolean).length + (multiCatScope3 ? activeCategories.length : 0);

    const firstCatTabIndex =
      (questionnaireData.configurations.directEmissionsShouldBeFilled ? 1 : 0) +
      (questionnaireData.configurations.indirectEmissionsShouldBeFilled ? 1 : 0);

    return <>
      <Tabs variant="unstyled" index={tabIndex} onChange={setTabIndex}>
          <TabList
            ref={tabListRef}
            display={tabCount <= 1 ? "none" : undefined}
            borderBottom="1px solid"
            borderColor="gray.200"
            overflowX="auto"
            overflowY="hidden"
            whiteSpace="nowrap"
            sx={{
              scrollbarWidth: "none",
              "::-webkit-scrollbar": { display: "none" },
              "& > button": {
                flexShrink: 0,
                mb: "-1px",
                py: 3,
                px: { base: 1.5, md: 2, lg: 3, xl: 4 },
                fontSize: { base: "13px", md: "14px", lg: "15px", xl: "16px" },
                fontWeight: "600",
                color: "gray.400",
                borderBottom: "2px solid transparent",
                whiteSpace: "nowrap",
                _selected: {
                  color: "gray.800",
                  borderBottom: "2px solid limegreen",
                },
              },
            }}
          >
            {questionnaireData.configurations.directEmissionsShouldBeFilled &&
              <Tab isDisabled={viewOnly && scopeHasData.scope1 === false} _disabled={{ opacity: 0.4, cursor: "not-allowed" }}>
                <TabLabelWithDot label={t("tabs.scope1")} hasData={scope1HasActivityData || scope1HasEmissionsData} />
              </Tab>
            }
            {questionnaireData.configurations.indirectEmissionsShouldBeFilled &&
              <Tab isDisabled={viewOnly && scopeHasData.scope2 === false} _disabled={{ opacity: 0.4, cursor: "not-allowed" }}>
                <TabLabelWithDot label={t("tabs.scope2")} hasData={scope2HasActivityData || scope2HasEmissionsData} />
              </Tab>
            }
            {scope3Visible && !multiCatScope3 &&
              <Tab isDisabled={viewOnly && scopeHasData.scope3 === false} _disabled={{ opacity: 0.4, cursor: "not-allowed" }}>
                <TabLabelWithDot label={t("tabs.scope3")} hasData={scope3HasAnyData} />
              </Tab>
            }
            {multiCatScope3 && activeCategories.map((catKey) => {
              const catNumber = CATEGORIES_STEPS.indexOf(catKey) + 1;
              return (
                <Tab key={catKey} isDisabled={viewOnly && scopeHasData.scope3 === false} _disabled={{ opacity: 0.4, cursor: "not-allowed" }}>
                  <TabLabelWithDot label={`Cat ${catNumber}`} hasData={!!scope3CatHasData[catKey]} />
                </Tab>
              );
            })}
          </TabList>
        <TabPanels>
          {
            questionnaireData.configurations.directEmissionsShouldBeFilled &&
            <TabPanel padding="0">
              <Heading as="h2" fontSize="24px" fontWeight="700" color="gray.600" mt={4} mb={2}>{t("scope1.info.title")}</Heading>
              <Text fontSize="14px" color="gray.500" mb={4} maxW="66%">{t("scope1.info.subtitle")}</Text>
              {!(isOpenedInModal || viewOnly || questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED) && (
                <DataTypeTabs
                  selected={dataTypes.scope1}
                  onChange={(type) => setDataTypes((prev) => ({ ...prev, scope1: type }))}
                  config={questionnaireData?.configurations}
                  hasActivityData={scope1HasActivityData}
                  hasEmissionsData={scope1HasEmissionsData}
                />
              )}
              {organizationStructure && <DirectEmissions
                organizationStructure={organizationStructure}
                defaultValues={questionnaireData}
                isPreview={isOpenedInModal || viewOnly ? true : questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED}
                canFinalize={taskHasContent}
                onHasData={(hasData) => handleScopeHasData("scope1", hasData)}
                onDownloadTemplate={(sectionType) => downloadScopeOneTemplate(sectionType)}
                onImport={handleDataImport}
                dataType={dataTypes.scope1}
                methodologyAndSourcesPanel={
                  <MethodologyAndSources
                    methodologyContent={questionnaireData?.methodologyMap?.scope1 ?? questionnaireData?.methodology}
                    onSaveMethodology={(content) => handleSaveMethodology("scope1", content)}
                    // Stationary / Mobile / Refrigerants each get their own tab and key (utils/methodologySubKeys).
                    // Local edits win over the fetched map, as for the single-methodology scopes.
                    methodologyMap={{ ...(questionnaireData?.methodologyMap || {}), ...(methodologyByScope || {}) }}
                    onSaveSubTypeMethodology={(subKey, content) => handleSaveMethodology(subKey, content)}
                    questionnaireId={questionnaireData?.id}
                    scopeKey="scope1"
                    isMethodologySaving={isMethodologySaving}
                    fileInfoList={filesByScope.scope1 ?? []}
                    onFileInfoListChange={(list) => handleFilesChange("scope1", list)}
                    folder={getFolderForQuestionnaire(questionnaireData?.id)}
                    readOnly={isOpenedInModal || viewOnly || questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED}
                  />
                }
              />}
            </TabPanel>
          }
          {questionnaireData.configurations.indirectEmissionsShouldBeFilled &&
            <TabPanel padding="0">
              <Heading as="h2" fontSize="24px" fontWeight="700" color="gray.600" mt={4} mb={2}>{t("scope2.info.title")}</Heading>
              <Text fontSize="14px" color="gray.500" mb={4} maxW="66%">{t("scope2.info.subtitle")}</Text>
              {!(isOpenedInModal || viewOnly || questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED) && (
                <DataTypeTabs
                  selected={dataTypes.scope2}
                  onChange={(type) => setDataTypes((prev) => ({ ...prev, scope2: type }))}
                  config={questionnaireData?.configurations}
                  hasActivityData={scope2HasActivityData}
                  hasEmissionsData={scope2HasEmissionsData}
                />
              )}
              {organizationStructure && <IndirectEmissions
                organizationStructure={organizationStructure}
                defaultValues={questionnaireData}
                isPreview={isOpenedInModal || viewOnly ? true : questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED}
                canFinalize={taskHasContent}
                onHasData={(hasData) => handleScopeHasData("scope2", hasData)}
                onDownloadTemplate={() => downloadScopeTwoTemplate()}
                onImport={handleDataImport}
                dataType={dataTypes.scope2}
                methodologyAndSourcesPanel={
                  <MethodologyAndSources
                    methodologyContent={questionnaireData?.methodologyMap?.scope2}
                    onSaveMethodology={(content) => handleSaveMethodology("scope2", content)}
                    questionnaireId={questionnaireData?.id}
                    scopeKey="scope2"
                    isMethodologySaving={isMethodologySaving}
                    fileInfoList={filesByScope.scope2 ?? []}
                    onFileInfoListChange={(list) => handleFilesChange("scope2", list)}
                    folder={getFolderForQuestionnaire(questionnaireData?.id)}
                    readOnly={isOpenedInModal || viewOnly || questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED}
                  />
                }
              />}
            </TabPanel>
          }
          {scope3Visible && !multiCatScope3 &&
            <TabPanel padding="0">
              <Heading as="h2" fontSize="24px" fontWeight="700" color="gray.600" mt={4} mb={2}>{t("scope3.info.title")}</Heading>
              <Text fontSize="14px" color="gray.500" mb={4} maxW="66%">{t("scope3.info.subtitle")}</Text>
              {!(isOpenedInModal || viewOnly || questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED) && (
                <DataTypeSelector
                  selected={dataTypes.scope3}
                  onChange={(type) => setDataTypes((prev) => ({ ...prev, scope3: type }))}
                  config={questionnaireData?.configurations}
                />
              )}
              <CategoryQuestionnaire
                defaultValues={questionnaireData}
                handleTabChange={setTabIndex}
                returnToListRequested={submitAndReturnToList}
                declineReturnToListRequest={() => setSubmitAndReturnToList(false)}
                returnToList={handleNavigationToList}
                allowedToSubmit={scope3Visible && tabIndex === indexes[TAB_TYPES.CATEGORIES]}
                isPreview={isOpenedInModal || viewOnly ? true : questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED}
                canFinalize={taskHasContent}
                onHasData={(hasData) => handleScopeHasData("scope3", hasData)}
              />
            </TabPanel>
          }
          {multiCatScope3 && activeCategories.map((catKey, catIdx) => {
            const catTitle = t(`scope3.categories.${catKey}`);
            const catDescRaw = t(`scope3.categories.${catKey}Desc`, { returnObjects: true });
            const catDesc = typeof catDescRaw === "string" ? catDescRaw : catDescRaw?.title || "";
            const catShowTabs = !(isOpenedInModal || viewOnly || questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED);
            return (
              <TabPanel key={catKey} padding="0">
                <Heading as="h2" fontSize="24px" fontWeight="700" color="gray.600" mt={4} mb={2}>{catTitle}</Heading>
                {catDesc && <Text fontSize="14px" color="gray.500" mb={4} maxW="66%">{catDesc}</Text>}
                <Scope3CategoryPanel
                  catKey={catKey}
                  defaultValues={questionnaireData}
                  onTabChange={setTabIndex}
                  canFinalize={taskHasContent}
                  returnToListRequested={submitAndReturnToList}
                  returnToList={handleNavigationToList}
                  declineReturnToListRequest={() => setSubmitAndReturnToList(false)}
                  allowedToSubmit={tabIndex === firstCatTabIndex + catIdx}
                  isPreview={isOpenedInModal || viewOnly ? true : questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED}
                  onHasData={(hasData) => handleScopeHasData("scope3", hasData)}
                  indexes={indexes}
                  dataType={dataTypes.scope3}
                  setDataType={(type) => setDataTypes((prev) => ({ ...prev, scope3: type }))}
                  configurations={questionnaireData?.configurations}
                  showDataTypeTabs={catShowTabs}
                  organizationStructure={organizationStructure}
                  onSaveAndClose={handleReturnToListClick}
                  methodologyAndSourcesPanel={
                    <MethodologyAndSources
                      methodologyContent={questionnaireData?.methodologyMap?.[catKey]}
                      onSaveMethodology={(content) => handleSaveMethodology(catKey, content)}
                      questionnaireId={questionnaireData?.id}
                      scopeKey={catKey}
                      isMethodologySaving={isMethodologySaving}
                      fileInfoList={filesByScope[catKey] ?? []}
                      onFileInfoListChange={(list) => handleFilesChange(catKey, list)}
                      folder={getFolderForQuestionnaire(questionnaireData?.id)}
                      readOnly={isOpenedInModal || viewOnly || questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED}
                    />
                  }
                />
              </TabPanel>
            );
          })}
        </TabPanels>
      </Tabs>
    </>;
  };

  if (isOpenedInModal &&
    (planningError || (!isPlanningDataLoading && !isPlanningDataFetching && typeof planningData === "undefined"))) {
    return <Flex blockSize="full">
      <Center flex="1">
        <Box textAlign="center">
          <Tag size="lg">{globalTranslation("somethingWentWrong")}</Tag>
        </Box>
      </Center>
    </Flex>;
  } else if (!isOpenedInModal && (
    (userRelatedError && userRelatedError.response.status === 404) ||
    (!isUserRelatedDataLoading && !isUserRelatedDataFetching && typeof userRelatedData === "undefined")
  )) {
    return <Flex blockSize="full">
      <Center flex="1">
        <Box textAlign="center">
          {userRelatedError && userRelatedError.response.status === 404 ?
            <>
              <Tag size="lg">{globalTranslation("noAvailableQuestionnaire")}</Tag>
              <Box mt={2}>
                <Flex
                  as="button"
                  align="center"
                  gap={2}
                  color="limeShade.600"
                  onClick={() => navigate(routes.dataCollectionOrganisationQuestionnaires)}
                  _hover={{ textDecoration: "underline" }}
                >
                  <FaArrowLeft />
                  <Text fontSize="sm">Back to list</Text>
                </Flex>
              </Box>
            </> :
            <Tag size="lg">{globalTranslation("somethingWentWrong")}</Tag>
          }
        </Box>
      </Center>
    </Flex>;
  } else {
    return questionnaireData && typeof questionnaireData !== "undefined" ?
      !isOpenedInModal ?
        <Flex direction="column" w="100%" minH="100vh">
          {/* Secondary header bar */}
          <Flex
            position="sticky"
            top={0}
            zIndex={10}
            bg="white"
            h="52px"
            flexShrink={0}
            align="center"
            justify="flex-end"
          >
            <Box
              borderLeft="1px solid"
              borderBottom="1px solid"
              borderColor="gray.200"
              h="100%"
              display="flex"
              alignItems="center"
              px={4}
              bg="white"
            >
              <Button
                variant="ghost"
                onClick={toggleTaskDetails}
                fontSize="14px"
                _hover={{ bg: "transparent" }}
                _active={{ bg: "transparent" }}
                leftIcon={<CaretLeft size={16} />}
                rightIcon={<Notepad size={16} color="var(--chakra-colors-limeShade-600)" />}
              >
                Task Details
              </Button>
            </Box>
          </Flex>

          {/* Page content */}
          <Grid templateColumns="1fr" bg="pureWhite" flex={1}>
            <Stack spacing={3} pt={2} pb={4} px={4} mx="10%" minW={0}>
              <ConfirmationDialog
                isOpen={isUnfinaliseConfirmOpen}
                onClose={onUnfinaliseConfirmClose}
                onConfirm={handleUnfinaliseConfirm}
                headerText="Unfinalise Task"
                confirmationText="Are you sure you want to unfinalise this task? It will be removed from emissions calculations until re-finalised."
              />
              {/* Back link */}
              <Flex align="center">
                <Button
                  variant="link"
                  color="limeShade.600"
                  leftIcon={<FaArrowLeft />}
                  onClick={handleReturnToListClick}
                  fontSize="16px"
                  fontWeight="600"
                  _hover={{ textDecoration: "none" }}
                >
                  <Text as="span" color="whiteShade.600" fontSize="16px" fontWeight="600">
                    Tasks
                  </Text>
                </Button>
              </Flex>

              {/* Heading + Tabs */}
              <Box>
                <Heading fontSize="24px" fontWeight="700" color="darkTeal" mb={2}>
                  {questionnaireData.description || "No task description added"}
                </Heading>
                {renderTabs()}
              </Box>
            </Stack>
          </Grid>
        </Flex> :
        renderTabs() : null;
  }
}

export default OrganizationQuestionnaire;
